import { useCallback, useMemo, useState } from "react";
import { Mail, Trash2, UserPlus, X } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import { useWorkspaceMembers } from "../../../../hooks/QueryHooks";
import { useAuthInfo } from "@propelauth/react";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import VariableChipEditor, { type VariableOption } from "./VariableChipEditor";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type EmailActionData = {
  type: "send_to_email";
  to?: string;
  subject?: string;
  body?: string;
};

type Props = {
  action: EmailActionData;
  onChange: (partial: Partial<EmailActionData>) => void;
  onRemove: () => void;
  variables: VariableOption[];
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const EmailActionCard = ({ action, onChange, onRemove, variables }: Props) => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id ?? "";
  const { user } = useAuthInfo();
  const [recipientPickerOpen, setRecipientPickerOpen] = useState(false);
  const [freeformEmail, setFreeformEmail] = useState("");

  /* --- current user email --- */
  const currentUserEmail = user?.email ?? "";

  /* --- workspace members --- */
  const { data: membersData } = useWorkspaceMembers({
    workspaceId,
    enabled: !!workspaceId,
  } as any);

  const memberEmails = useMemo(() => {
    const list: any[] = (membersData as any)?.members ?? (membersData as any)?.users ?? [];
    return list.map((m: any) => ({
      email: m.email,
      name: m.first_name && m.last_name ? `${m.first_name} ${m.last_name}` : m.email,
    }));
  }, [membersData]);

  /* --- parse current "to" into array --- */
  const recipients = useMemo(() => {
    if (!action.to) return [];
    return action.to
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
  }, [action.to]);

  const setRecipients = useCallback(
    (emails: string[]) => {
      onChange({ to: emails.join(", ") });
    },
    [onChange],
  );

  const addRecipient = useCallback(
    (email: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed) return;
      if (recipients.includes(trimmed)) return;
      setRecipients([...recipients, trimmed]);
    },
    [recipients, setRecipients],
  );

  const removeRecipient = useCallback(
    (email: string) => {
      setRecipients(recipients.filter((e) => e !== email));
    },
    [recipients, setRecipients],
  );

  const handleFreeformAdd = () => {
    if (!freeformEmail.trim()) return;
    // Basic email validation
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(freeformEmail.trim())) {
      addRecipient(freeformEmail);
      setFreeformEmail("");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Mail className="h-4 w-4" /> Email Notification
        </CardTitle>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Recipients */}
        <div>
          <Label className="text-xs">Recipients</Label>
          <p className="mb-2 text-[10px] text-muted-foreground">
            Add yourself, workspace members, or enter any email address.
          </p>

          {/* Recipient chips */}
          <div className="mb-2 flex flex-wrap gap-1.5">
            {recipients.map((email) => (
              <Badge key={email} variant="secondary" className="gap-1 text-[11px]">
                {email}
                <button
                  type="button"
                  onClick={() => removeRecipient(email)}
                  className="ml-0.5 opacity-60 hover:opacity-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {recipients.length === 0 && (
              <span className="text-xs text-muted-foreground">No recipients added</span>
            )}
          </div>

          {/* Add recipients */}
          <div className="flex gap-2">
            {/* Quick add: me */}
            {currentUserEmail && !recipients.includes(currentUserEmail) && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => addRecipient(currentUserEmail)}
              >
                Send to me
              </Button>
            )}

            {/* Add from workspace members */}
            <Popover open={recipientPickerOpen} onOpenChange={setRecipientPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-7 text-xs">
                  <UserPlus className="mr-1 h-3 w-3" />
                  Add member
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-1" align="start">
                <div className="max-h-[200px] overflow-y-auto">
                  {memberEmails.length === 0 && (
                    <p className="px-2 py-3 text-center text-xs text-muted-foreground">No members found</p>
                  )}
                  {memberEmails.map((member) => {
                    const isAdded = recipients.includes(member.email);
                    return (
                      <button
                        key={member.email}
                        type="button"
                        disabled={isAdded}
                        className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-muted disabled:opacity-50"
                        onClick={() => {
                          addRecipient(member.email);
                        }}
                      >
                        <div>
                          <span className="font-medium">{member.name}</span>
                          <span className="ml-1.5 text-muted-foreground">{member.email}</span>
                        </div>
                        {isAdded && <span className="text-[10px] text-muted-foreground">Added</span>}
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>

            {/* Free-form email input */}
            <div className="flex flex-1 gap-1">
              <Input
                value={freeformEmail}
                onChange={(e) => setFreeformEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleFreeformAdd();
                  }
                }}
                placeholder="Enter email..."
                className="h-7 text-xs"
              />
              <Button
                variant="outline"
                size="sm"
                className="h-7 shrink-0 text-xs"
                onClick={handleFreeformAdd}
                disabled={!freeformEmail.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Subject */}
        <div>
          <Label className="text-xs">Subject</Label>
          <VariableChipEditor
            value={action.subject || ""}
            onChange={(subj) => onChange({ subject: subj })}
            variables={variables}
            placeholder="e.g. Alert: {{accounts.name}} health score changed"
            rows={1}
          />
        </div>

        {/* Body */}
        <div>
          <Label className="text-xs">Body</Label>
          <VariableChipEditor
            value={action.body || ""}
            onChange={(body) => onChange({ body })}
            variables={variables}
            placeholder="e.g. Account {{accounts.name}} has been flagged..."
            rows={4}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailActionCard;
